import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import {
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { toast } from 'sonner';

import { DestinationSetupSection } from '@/components/treatment/v3/provider/DestinationSetupSection';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  useGetMedicalProviderQuery,
  useUpdateMedicalProviderMutation,
} from '@/services/api/treatmentService';
import {
  MedicalProvider,
  ProviderStatus,
  ProviderTreatmentStatus,
} from '@/services/types/treatment-types';

import { BasicInfoTab } from '../forms/BasicInfoTab';
import { ContactInfoTab } from '../forms/ContactInfoTab';
import { ProviderFormTabs } from '../forms/ProviderFormTabs';
import { TreatmentDetailsTab } from '../forms/TreatmentDetailsTab';
import { ProviderFormData } from '../forms/types';
import { getSimpleTreatmentStatus, SimpleTreatmentStatus } from '../utils';

export const EditProviderPage = () => {
  const { matter_id, provider_id } = useParams<{
    matter_id: string;
    provider_id: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [updateProvider, { isLoading: isUpdating }] =
    useUpdateMedicalProviderMutation();
  const { data: provider, isLoading: isLoadingProvider } =
    useGetMedicalProviderQuery(
      {
        matterId: matter_id ?? '',
        providerId: provider_id ?? '',
      },
      { skip: !matter_id || !provider_id },
    );
  const [activeTab, setActiveTab] = useState<'basic' | 'contact' | 'details'>(
    'basic',
  );
  const requestedTab = searchParams.get('tab');
  const statusOptions = [
    {
      value: ProviderStatus.NEEDS_APPROVAL,
      label: 'Pending',
      description: 'Awaiting review or confirmation',
    },
    {
      value: ProviderStatus.APPROVED,
      label: 'Approved',
      description: 'Ready for tasking and records requests',
    },
    {
      value: ProviderStatus.REJECTED,
      label: 'Rejected',
      description: 'Provider is not relevant for this matter',
    },
  ];

  const { register, handleSubmit, setValue, watch, control } =
    useForm<ProviderFormData>({
      defaultValues: {
        name: '',
        provider_type: null,
        address: '',
        treatment: '',
        practitioner_names: [],
        handles: [],
        status: ProviderStatus.NEEDS_APPROVAL,
        treatment_status: ProviderTreatmentStatus.UNKNOWN,
        date_of_records_request: '',
        description: '',
      },
    });

  const {
    fields: practitionerFields,
    append: appendPractitioner,
    remove: removePractitioner,
  } = useFieldArray({
    control,
    name: 'practitioner_names',
  });
  const {
    fields: handleFields,
    append: appendHandle,
    remove: removeHandle,
  } = useFieldArray({
    control,
    name: 'handles',
  });

  useEffect(() => {
    if (
      requestedTab === 'basic' ||
      requestedTab === 'contact' ||
      requestedTab === 'details'
    ) {
      setActiveTab(requestedTab);
    }
  }, [requestedTab]);

  // Populate form with existing provider data
  useEffect(() => {
    if (provider) {
      setValue('name', provider.name || '');
      setValue('provider_type', provider.provider_type || null);
      setValue('address', provider.address || '');
      setValue('treatment', provider.treatment || '');
      setValue('description', provider.description || '');
      setValue(
        'date_of_records_request',
        provider.date_of_records_request || '',
      );
      setValue('status', provider.status || ProviderStatus.NEEDS_APPROVAL);

      // Convert still_treating string to enum
      const currentStatus = getSimpleTreatmentStatus(provider.treatment_status);
      if (currentStatus === SimpleTreatmentStatus.TREATING) {
        setValue('treatment_status', ProviderTreatmentStatus.TREATING);
      } else if (currentStatus === SimpleTreatmentStatus.COMPLETED) {
        setValue('treatment_status', ProviderTreatmentStatus.COMPLETED);
      } else {
        setValue('treatment_status', ProviderTreatmentStatus.UNKNOWN);
      }

      // Handle practitioner names - convert from string[] to {name: string}[]
      const practitionerNames =
        provider.practitioner_names?.map((name) => ({
          name,
        })) || [];
      setValue('practitioner_names', practitionerNames);
      setValue('handles', provider.handles ?? []);
    }
  }, [provider, setValue]);

  const onSubmit = async (data: ProviderFormData) => {
    if (!matter_id || !provider_id || !provider) return;

    try {
      // Convert form data to API format
      const updateData = {
        name: data.name,
        provider_type: data.provider_type,
        address: data.address || null,
        treatment: data.treatment || null,
        description: data.description || null,
        status: data.status || ProviderStatus.NEEDS_APPROVAL,
        practitioner_names:
          data.practitioner_names
            .map((p) => p.name)
            .filter((name) => name.trim()) || null,
        handles:
          data.handles
            ?.map((handle) => ({
              ...handle,
              value: handle.value.trim(),
              name: handle.name?.trim() || undefined,
            }))
            .filter((handle) => handle.value.length > 0) || [],
        treatment_status: data.treatment_status,
        date_of_records_request: data.date_of_records_request || null,
      };

      await updateProvider({
        matterId: matter_id,
        providerId: provider_id,
        provider: updateData as MedicalProvider,
      }).unwrap();

      toast.success('Medical provider updated successfully');
      navigate(-1);
    } catch (error) {
      toast.error('Failed to update medical provider');
    }
  };

  // Check if required fields are filled for basic tab
  const isBasicInfoComplete = () => {
    const formData = watch();
    return !!formData.name?.trim();
  };

  const canSubmit = isBasicInfoComplete();
  const approvalStatus = watch('status');

  const handleCancel = () => {
    navigate(-1);
  };

  const handleStatusSelect = (status: ProviderStatus) => {
    setValue('status', status, { shouldDirty: true });
  };

  // Smart tab navigation
  const handleNextTab = () => {
    const tabOrder = ['basic', 'contact', 'details'] as const;
    const currentIndex = tabOrder.indexOf(activeTab);
    if (currentIndex < tabOrder.length - 1) {
      setActiveTab(tabOrder[currentIndex + 1]);
    }
  };

  const handlePreviousTab = () => {
    const tabOrder = ['basic', 'contact', 'details'] as const;
    const currentIndex = tabOrder.indexOf(activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabOrder[currentIndex - 1]);
    }
  };

  if (!matter_id || !provider_id) {
    return <div>Invalid parameters</div>;
  }

  if (isLoadingProvider) {
    return <div>Loading provider...</div>;
  }

  if (!provider) {
    return <div>Provider not found</div>;
  }

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4" />
              Go Back
            </Button>
            <div className="h-6 w-px bg-gray-300" />
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-semibold text-gray-900">
                  Edit Medical Provider
                </h1>
                {!canSubmit && (
                  <Badge
                    variant="outline"
                    className="text-amber-600 border-amber-300"
                  >
                    Incomplete
                  </Badge>
                )}
                {canSubmit && (
                  <Badge
                    variant="outline"
                    className="text-green-600 border-green-300"
                  >
                    Ready to Save
                  </Badge>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Update the medical provider information for this matter
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Provider ID:</span>
            <code className="bg-gray-100 px-2 py-1 rounded text-xs">
              {provider_id}
            </code>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6">
              <ProviderFormTabs
                activeTab={activeTab}
                onTabChange={setActiveTab}
                watch={watch}
              />
              <div className="mt-6 border border-gray-200 rounded-lg p-4 bg-gray-50 flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-semibold text-gray-700">
                    Approval Status
                  </span>
                  <span className="text-sm text-gray-500">
                    Choose how this provider should appear in Worklists.
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {statusOptions.map((option) => (
                    <Button
                      key={option.value}
                      type="button"
                      variant={
                        approvalStatus === option.value ? 'default' : 'outline'
                      }
                      className="h-auto min-h-[78px] w-full flex-col items-start gap-1 px-4 py-3 whitespace-normal text-left"
                      onClick={() => handleStatusSelect(option.value)}
                    >
                      <span
                        className={
                          approvalStatus === option.value
                            ? 'text-sm font-semibold text-white'
                            : 'text-sm font-semibold text-gray-900'
                        }
                      >
                        {option.label}
                      </span>
                      <span
                        className={
                          approvalStatus === option.value
                            ? 'text-xs text-indigo-100'
                            : 'text-xs text-gray-500'
                        }
                      >
                        {option.description}
                      </span>
                    </Button>
                  ))}
                </div>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
                <div className="min-h-[500px]">
                  {activeTab === 'basic' && (
                    <BasicInfoTab
                      register={register}
                      setValue={setValue}
                      watch={watch}
                      practitionerFields={practitionerFields}
                      appendPractitioner={appendPractitioner}
                      removePractitioner={removePractitioner}
                    />
                  )}

                  {activeTab === 'contact' && (
                    <div className="space-y-4">
                      <ContactInfoTab
                        register={register}
                        setValue={setValue}
                        watch={watch}
                        handleFields={handleFields}
                        appendHandle={appendHandle}
                        removeHandle={removeHandle}
                      />

                      <div className="border-t border-zinc-200 pt-6">
                        <div className="mb-3">
                          <h3 className="text-sm font-semibold text-zinc-900">
                            Request Destinations
                          </h3>
                          <p className="text-sm text-zinc-500">
                            Manage destination defaults used for record requests.
                          </p>
                        </div>

                        <DestinationSetupSection provider={provider} />
                      </div>
                    </div>
                  )}

                  {activeTab === 'details' && (
                    <TreatmentDetailsTab
                      register={register}
                      setValue={setValue}
                      watch={watch}
                    />
                  )}
                </div>

                {/* Enhanced Actions */}
                <div className="flex items-center justify-between pt-6 mt-6 border-t bg-gray-50 -m-6 p-6 rounded-b-lg">
                  {/* Tab Navigation */}
                  <div className="flex gap-2">
                    {activeTab !== 'basic' && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handlePreviousTab}
                        disabled={isUpdating}
                      >
                        Previous
                      </Button>
                    )}
                    {activeTab !== 'details' && isBasicInfoComplete() && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleNextTab}
                        disabled={isUpdating}
                      >
                        Next
                      </Button>
                    )}
                  </div>

                  {/* Primary Actions */}
                  <div className="flex items-center gap-3">
                    {!canSubmit && (
                      <div className="text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded border border-amber-200">
                        Complete the Basic Info tab to save changes
                      </div>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCancel}
                      disabled={isUpdating}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isUpdating || !canSubmit}
                      className="min-w-32"
                    >
                      {isUpdating ? 'Updating...' : 'Update Provider'}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
